import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MapPin, Mail, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import GuestPortalFooter from "../components/GuestPortalFooter";
import { setAgentThemeColor } from "../utils/agentTheme";
import { StepProgressIndicator } from "../components/StepProgressIndicator";
import { DatePickerField } from "../components/DatePickerField";
import { API, BACKEND_URL } from "../config/api";


// Helper to get or create a unique visitor ID
const getVisitorId = () => {
  const storageKey = 'mcp_visitor_id';
  let visitorId = localStorage.getItem(storageKey);
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem(storageKey, visitorId);
  }
  return visitorId;
};

// Helper to track analytics events
const trackAnalyticsEvent = async (agentId, agentSlug, eventType, destination = null, metadata = null) => {
  try {
    const visitorId = getVisitorId();
    await axios.post(`${API}/analytics/track`, {
      agent_id: agentId,
      agent_slug: agentSlug,
      event_type: eventType,
      visitor_id: visitorId,
      destination: destination,
      metadata: metadata
    });
  } catch (error) {
    // Silently fail - analytics shouldn't break the user experience
    console.log('Analytics tracking failed:', error.message);
  }
};

// Helper to resolve image URLs
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Handle both old format (/uploads/...) and new format (/api/uploads/...)
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
  if (url.startsWith('/api/uploads/')) return `${BACKEND_URL}${url}`;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

export default function AgentGuestPortalPage() {
  const navigate = useNavigate();
  const { agentSlug } = useParams();
  const [agent, setAgent] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDestination, setSelectedDestination] = useState(() => sessionStorage.getItem("guestForm_destination") || "");
  const [selectedDestinationData, setSelectedDestinationData] = useState(null);
  const [step, setStep] = useState("destination"); // destination, credentials, confirmation
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedData, setSubmittedData] = useState(null); // Store submitted booking data
  const [showLookup, setShowLookup] = useState(false);
  const [lookupEmail, setLookupEmail] = useState("");
  const [isLookingUp, setIsLookingUp] = useState(false);

  const handleLookupBooking = async () => {
    if (!lookupEmail.trim()) return;
    setIsLookingUp(true);
    try {
      const res = await axios.post(`${API}/bookings/lookup`, { email: lookupEmail.trim() });
      navigate(`/my-booking/${res.data.magic_token}`);
    } catch {
      toast.error("No booking found for this email. Please check and try again.");
    } finally {
      setIsLookingUp(false);
    }
  };
  
  // Guest form fields — restore from sessionStorage for back-navigation persistence
  const [firstName, setFirstName] = useState(() => sessionStorage.getItem("guestForm_firstName") || "");
  const [lastName, setLastName] = useState(() => sessionStorage.getItem("guestForm_lastName") || "");
  const [email, setEmail] = useState(() => sessionStorage.getItem("guestForm_email") || "");
  const [villaName, setVillaName] = useState(() => sessionStorage.getItem("guestForm_villaName") || "");
  const [villaAddress, setVillaAddress] = useState(() => sessionStorage.getItem("guestForm_villaAddress") || "");
  const [villaLink, setVillaLink] = useState(() => sessionStorage.getItem("guestForm_villaLink") || "");
  const [bookingReference, setBookingReference] = useState(() => sessionStorage.getItem("guestForm_bookingReference") || "");
  const [arrivalDate, setArrivalDate] = useState(() => sessionStorage.getItem("guestForm_arrivalDate") || "");
  const [departureDate, setDepartureDate] = useState(() => sessionStorage.getItem("guestForm_departureDate") || "");
  const [numAdults, setNumAdults] = useState(() => parseInt(sessionStorage.getItem("guestForm_numAdults")) || 2);
  const [numChildren, setNumChildren] = useState(() => parseInt(sessionStorage.getItem("guestForm_numChildren") ?? "") || 0);
  const [isSavingLater, setIsSavingLater] = useState(false);
  const [lastMinuteConfig, setLastMinuteConfig] = useState(null);
  const [lastMinuteTier, setLastMinuteTier] = useState(null); // { fee, label, daysAway }
  const [lastMinuteBlocked, setLastMinuteBlocked] = useState(false);
  const [lastMinuteAccepted, setLastMinuteAccepted] = useState(false);
  const [existingBooking, setExistingBooking] = useState(null);
  const [submittedBookings, setSubmittedBookings] = useState([]);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);

  // Persist form fields to sessionStorage
  const persistField = useCallback((key, value) => {
    sessionStorage.setItem(`guestForm_${key}`, String(value));
  }, []);

  useEffect(() => { persistField("firstName", firstName); }, [firstName, persistField]);
  useEffect(() => { persistField("lastName", lastName); }, [lastName, persistField]);
  useEffect(() => { persistField("email", email); }, [email, persistField]);
  useEffect(() => { persistField("villaName", villaName); }, [villaName, persistField]);
  useEffect(() => { persistField("villaAddress", villaAddress); }, [villaAddress, persistField]);
  useEffect(() => { persistField("villaLink", villaLink); }, [villaLink, persistField]);
  useEffect(() => { persistField("bookingReference", bookingReference); }, [bookingReference, persistField]);
  useEffect(() => { persistField("arrivalDate", arrivalDate); }, [arrivalDate, persistField]);
  useEffect(() => { persistField("departureDate", departureDate); }, [departureDate, persistField]);
  useEffect(() => { persistField("numAdults", numAdults); }, [numAdults, persistField]);
  useEffect(() => { persistField("numChildren", numChildren); }, [numChildren, persistField]);

  // Load last-minute fee config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const resp = await axios.get(`${API}/last-minute-fees/config`);
        setLastMinuteConfig(resp.data);
      } catch (err) {
        console.error("Failed to load last-minute config:", err);
      }
    };
    loadConfig();
  }, []);

  // Check arrival date against last-minute tiers whenever it changes
  useEffect(() => {
    if (!arrivalDate || !lastMinuteConfig || !lastMinuteConfig.enabled) {
      setLastMinuteTier(null);
      setLastMinuteBlocked(false);
      setLastMinuteAccepted(false);
      return;
    }
    
    const arrival = new Date(arrivalDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    arrival.setHours(0, 0, 0, 0);
    const daysAway = Math.floor((arrival - today) / (1000 * 60 * 60 * 24));
    
    // Check if blocked (same day / next day = 0 or 1 days)
    if (lastMinuteConfig.block_same_day && daysAway <= 0) {
      setLastMinuteBlocked(true);
      setLastMinuteTier(null);
      setLastMinuteAccepted(false);
      return;
    }
    
    // Check tiers
    setLastMinuteBlocked(false);
    const tiers = lastMinuteConfig.tiers || [];
    let matched = null;
    for (const tier of tiers) {
      if (daysAway >= tier.min_days && daysAway <= tier.max_days) {
        matched = { ...tier, daysAway };
        break;
      }
    }
    setLastMinuteTier(matched);
    if (!matched) setLastMinuteAccepted(false);
  }, [arrivalDate, lastMinuteConfig]);

  useEffect(() => {
    const loadAgentAndDestinations = async () => {
      // Retry logic for resilience against transient failures
      let lastError;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const destResponse = await axios.get(`${API}/agents/${agentSlug}/destinations`);
          const { destinations: enabledDests, auto_select, selected_destination, agent: agentInfo } = destResponse.data;
          
          const agentResponse = await axios.get(`${API}/agents/${agentSlug}`);
          const agentData = agentResponse.data;
          
          setAgent(agentData);
          setDestinations(enabledDests);
        
        // Track portal visit
        trackAnalyticsEvent(agentData.id, agentSlug, 'visit');
        
        // Set agent theme color (used by all .btn-agent-primary buttons)
        setAgentThemeColor(agentData.button_color);
        
        // Apply brand styles as CSS variables
        if (agentData.brand_styles) {
          document.documentElement.style.setProperty('--agent-primary', agentData.brand_styles.primaryColor);
          document.documentElement.style.setProperty('--agent-bg', agentData.brand_styles.backgroundColor);
        }
        
        // Auto-select if only one destination is enabled
        if (auto_select && selected_destination) {
          setSelectedDestination(selected_destination.slug);
          setSelectedDestinationData(selected_destination);
          setStep("credentials");
        } else {
          // Restore destination from sessionStorage if user navigated back
          const savedDest = sessionStorage.getItem("guestForm_destination");
          if (savedDest && enabledDests.length > 0) {
            const matchedDest = enabledDests.find(d => d.slug === savedDest);
            if (matchedDest) {
              setSelectedDestination(matchedDest.slug);
              setSelectedDestinationData(matchedDest);
              setStep("credentials");
            }
          }
        }
          lastError = null;
          break; // Success — exit retry loop
        } catch (error) {
          lastError = error;
          if (attempt < 3) {
            await new Promise(r => setTimeout(r, 1000 * attempt));
            continue;
          }
        }
      }
      if (lastError) {
        toast.error("Portal not found");
        navigate("/");
      }
      setIsLoading(false);
    };

    loadAgentAndDestinations();

    return () => {
      // Reset styles on unmount
      document.documentElement.style.removeProperty('--agent-primary');
      document.documentElement.style.removeProperty('--agent-bg');
    };
  }, [agentSlug, navigate]);

  const handleDestinationSelect = (dest) => {
    setSelectedDestination(dest.slug);
    setSelectedDestinationData(dest);
    sessionStorage.setItem("guestForm_destination", dest.slug);
    setStep("credentials");
  };

  const checkExistingBooking = async (emailValue) => {
    const trimmed = emailValue.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
    try {
      const res = await axios.get(`${API}/bookings/check-existing?email=${encodeURIComponent(trimmed)}&agent_slug=${agentSlug}`);
      if (res.data.found) {
        const hasSubmitted = (res.data.submitted_bookings || []).length > 0;
        const hasInProgress = !!res.data.in_progress_booking;
        setExistingBooking(res.data.in_progress_booking || null);
        setSubmittedBookings(res.data.submitted_bookings || []);
        // Show full welcome back screen if they have any bookings
        if (hasSubmitted || hasInProgress) {
          setShowWelcomeBack(true);
        }
      } else {
        setExistingBooking(null);
        setSubmittedBookings([]);
        setShowWelcomeBack(false);
      }
    } catch {
      setExistingBooking(null);
      setSubmittedBookings([]);
      setShowWelcomeBack(false);
    }
  };

  const handleResumeBooking = () => {
    const b = existingBooking;
    // Store booking data in sessionStorage so the app picks it up
    sessionStorage.setItem("bookingData", JSON.stringify(b));
    sessionStorage.setItem("guestForm_firstName", b.first_name || "");
    sessionStorage.setItem("guestForm_lastName", b.last_name || "");
    sessionStorage.setItem("guestForm_email", b.email || "");
    sessionStorage.setItem("guestForm_villaName", b.villa_name || "");
    sessionStorage.setItem("guestForm_villaAddress", b.villa_address || "");
    sessionStorage.setItem("guestForm_villaLink", b.villa_link || "");
    sessionStorage.setItem("guestForm_destination", b.destination || "");
    sessionStorage.setItem("guestForm_arrivalDate", b.arrival_date || "");
    sessionStorage.setItem("guestForm_departureDate", b.departure_date || "");
    sessionStorage.setItem("guestForm_numAdults", String(b.num_adults || 2));
    sessionStorage.setItem("guestForm_numChildren", String(b.num_children || 0));
    sessionStorage.setItem("guestForm_bookingReference", b.booking_reference || "");
    // Store agent info
    if (agent) {
      sessionStorage.setItem("agentData", JSON.stringify({
        id: agent.id,
        agent_slug: agent.agent_slug,
        agent_name: agent.agent_name,
        logo_url: agent.logo_url,
        brand_styles: agent.brand_styles,
        enabled_destinations: agent.enabled_destinations || ["mallorca", "ibiza"],
        show_yachts: agent.show_yachts,
        show_yachts_by_destination: agent.show_yachts_by_destination || {},
        show_water_experiences: agent.show_water_experiences,
        button_color: agent.button_color
      }));
    }
    toast.success("Welcome back! Continuing where you left off.");
    navigate("/services");
  };

  const handleAccessPortal = async () => {
    // Validate all required fields
    if (!firstName.trim() || !lastName.trim() || !email.trim() || 
        !villaName.trim() || !arrivalDate || !departureDate) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Validate group size - at least 1 adult required
    if (numAdults < 1) {
      toast.error("At least 1 adult is required");
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    // Validate dates
    if (new Date(departureDate) <= new Date(arrivalDate)) {
      toast.error("Departure date must be after arrival date");
      return;
    }
    
    // Block same-day / next-day bookings
    if (lastMinuteBlocked) {
      return; // UI already shows the block message
    }
    
    // Require last-minute fee acceptance if applicable
    if (lastMinuteTier && !lastMinuteAccepted) {
      toast.error("Please acknowledge the last minute planning fee to continue");
      return;
    }

    setIsSubmitting(true);
    try {
      // Create new guest submission
      const response = await axios.post(`${API}/bookings/gate`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        villa_name: villaName.trim(),
        villa_address: villaAddress.trim(),
        villa_link: villaLink.trim(),
        booking_reference: bookingReference.trim(),
        arrival_date: arrivalDate,
        departure_date: departureDate,
        destination: selectedDestination,
        agent_id: agent.id,
        agent_slug: agent.agent_slug,
        num_adults: numAdults,
        num_children: numChildren
      });

      // Store booking and agent context
      const bookingData = {
        ...response.data,
        agent_id: agent.id,
        agent_slug: agent.agent_slug,
        agent_name: agent.agent_name,
        show_yachts: agent.show_yachts,
        show_yachts_by_destination: agent.show_yachts_by_destination || {},
        show_water_experiences: agent.show_water_experiences,
        button_color: agent.button_color,
        num_adults: numAdults,
        num_children: numChildren
      };
      
      sessionStorage.setItem("bookingData", JSON.stringify(bookingData));
      sessionStorage.setItem("agentContext", JSON.stringify({
        id: agent.id,
        agent_slug: agent.agent_slug,
        agent_name: agent.agent_name,
        agent_type: agent.agent_type,
        logo_url: agent.logo_url,
        brand_styles: agent.brand_styles,
        enabled_destinations: agent.enabled_destinations || ["mallorca", "ibiza"],
        show_yachts: agent.show_yachts,
        show_yachts_by_destination: agent.show_yachts_by_destination || {},
        show_water_experiences: agent.show_water_experiences,
        button_color: agent.button_color,
        powered_by_brand: agent.powered_by_brand,
        privacy_policy_url: agent.privacy_policy_url,
        terms_conditions_url: agent.terms_conditions_url
      }));

      // Store submitted data and show confirmation
      setSubmittedData({
        firstName: firstName.trim(),
        villaName: villaName.trim()
      });
      setStep("confirmation");
    } catch (error) {
      toast.error("Failed to submit details. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndContinueLater = async () => {
    // Validate email at minimum
    if (!email.trim()) {
      toast.error("Please enter your email address so we can send you a link");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Please enter your name so we can save your details");
      return;
    }

    setIsSavingLater(true);
    try {
      const res = await axios.post(`${API}/bookings/save-and-continue-later`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        villa_name: villaName.trim(),
        villa_address: villaAddress.trim(),
        villa_link: villaLink.trim(),
        booking_reference: bookingReference.trim(),
        arrival_date: arrivalDate,
        departure_date: departureDate,
        destination: selectedDestination,
        agent_id: agent.id,
        agent_slug: agent.agent_slug,
        num_adults: numAdults,
        num_children: numChildren
      });
      toast.success("We've sent you an email with a link to continue planning your stay.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to save. Please try again.");
    } finally {
      setIsSavingLater(false);
    }
  };

  const handleProceedToServices = () => {
    navigate("/services");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--agent-primary, #8B7355)' }} />
      </div>
    );
  }

  if (!agent) return null;

  // Get full logo URL
  const getLogoUrl = () => {
    if (!agent.logo_url) return null;
    if (agent.logo_url.startsWith('/')) {
      return `${BACKEND_URL}${agent.logo_url}`;
    }
    return agent.logo_url;
  };

  // Get destination display name
  const getDestinationName = () => {
    if (selectedDestinationData) {
      return selectedDestinationData.name;
    }
    return selectedDestination.charAt(0).toUpperCase() + selectedDestination.slice(1);
  };

  // Check if we should show back button (only if multiple destinations)
  const showBackButton = destinations.length > 1;

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Agent Logo/Name */}
      <header className="pt-12 pb-8 text-center">
        {getLogoUrl() ? (
          <img 
            src={getLogoUrl()} 
            alt={agent.agent_name}
            className="h-16 md:h-20 mx-auto object-contain"
            style={{ maxWidth: '240px' }}
          />
        ) : (
          <h1 
            className="text-2xl md:text-3xl font-serif tracking-wide"
            style={{ color: agent?.brand_styles?.primaryColor || 'inherit' }}
          >
            {agent.agent_name}
          </h1>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-xl mx-auto px-6 pb-20">
        {step === "destination" && destinations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-serif text-foreground mb-4">
              {agent.headline || "Your Stay, Your Way"}
            </h2>
            <p className="text-base text-muted-foreground mb-12 max-w-md mx-auto">
              {agent.intro_copy || "Let us help you plan the perfect stay."}
            </p>

            <p className="text-sm text-muted-foreground mb-6">Select your destination</p>

            <div className="grid gap-4">
              {destinations.map((dest) => (
                <button
                  key={dest.id}
                  onClick={() => handleDestinationSelect(dest)}
                  data-testid={`destination-${dest.slug}`}
                  className="group relative overflow-hidden rounded-2xl aspect-[21/9]"
                >
                  <img
                    src={getImageUrl(dest.hero_image) || `https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=1200`}
                    alt={dest.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ filter: 'brightness(0.95)' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                    <MapPin className="w-4 h-4" />
                    <span className="text-lg font-serif">{dest.name}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Returning client lookup */}
            <div className="mt-12 pt-8 border-t border-border/30 text-center">
              {!showLookup ? (
                <button
                  onClick={() => setShowLookup(true)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="view-existing-booking-link"
                >
                  Already have a booking? <span className="underline">View it here</span>
                </button>
              ) : (
                <div className="max-w-sm mx-auto space-y-3">
                  <p className="text-sm text-muted-foreground mb-3">Enter the email you used when booking</p>
                  <input
                    type="email"
                    value={lookupEmail}
                    onChange={(e) => setLookupEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLookupBooking()}
                    placeholder="Your email address"
                    className="form-input w-full"
                    data-testid="lookup-email-input"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowLookup(false); setLookupEmail(""); }}
                      className="flex-1 px-4 py-2.5 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted/20 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleLookupBooking}
                      disabled={isLookingUp || !lookupEmail.trim()}
                      className="flex-1 btn-agent-primary px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                      data-testid="lookup-submit-btn"
                    >
                      {isLookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : "View My Booking"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {step === "credentials" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {showBackButton && (
              <button
                onClick={() => setStep("destination")}
                className="text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
                data-testid="back-to-destinations"
              >
                ← Back to destinations
              </button>
            )}

            {/* Step Progress Indicator */}
            <StepProgressIndicator currentStep={1} />

            <h2 className="text-2xl md:text-3xl font-serif text-foreground mb-4 mt-4 text-center">
              Plan your stay in {getDestinationName()}
            </h2>
            <p className="text-base text-muted-foreground mb-10 text-center">
              Please enter your details so we can prepare your concierge itinerary.
            </p>

            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    data-testid="first-name-input"
                    className="form-input"
                    placeholder="e.g., John"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    data-testid="last-name-input"
                    className="form-input"
                    placeholder="e.g., Smith"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={(e) => checkExistingBooking(e.target.value)}
                  data-testid="email-input"
                  className="form-input"
                  placeholder="e.g., john.smith@email.com"
                  required
                />
              </div>

              {/* Welcome Back Screen — shows when client has existing bookings */}
              {showWelcomeBack && (
                <div className="col-span-1 md:col-span-2 bg-white border border-border/50 rounded-xl p-6 shadow-sm" data-testid="welcome-back-section">
                  <h3 className="text-lg font-semibold text-foreground mb-1">Welcome back!</h3>
                  <p className="text-sm text-muted-foreground mb-5">We found existing bookings for your email. What would you like to do?</p>

                  {/* In-progress booking */}
                  {existingBooking && (
                    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm font-medium text-foreground mb-1">Booking in progress</p>
                      <p className="text-xs text-muted-foreground mb-3">
                        {existingBooking.villa_name || "Your trip"}{existingBooking.destination ? ` · ${existingBooking.destination.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}` : ""}
                        {existingBooking.selections?.length > 0 ? ` · ${existingBooking.selections.length} service${existingBooking.selections.length > 1 ? "s" : ""} selected` : ""}
                      </p>
                      <button
                        type="button"
                        onClick={handleResumeBooking}
                        data-testid="resume-booking-btn"
                        className="btn-agent-primary px-4 py-2.5 rounded-lg text-sm font-medium w-full"
                      >
                        Continue where I left off
                      </button>
                    </div>
                  )}

                  {/* Submitted bookings list */}
                  {submittedBookings.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-2">Your bookings</p>
                      <div className="space-y-2">
                        {submittedBookings.map(b => (
                          <button
                            key={b.id}
                            type="button"
                            onClick={() => navigate(`/my-booking/${b.magic_token}`)}
                            className="w-full text-left p-3 border border-border/50 rounded-lg hover:border-primary/30 hover:bg-primary/5 transition-colors"
                            data-testid={`submitted-booking-${b.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-foreground">
                                  {b.villa_name || b.destination?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "Your Trip"}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {b.reference_number}{b.arrival_date ? ` · ${new Date(b.arrival_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
                                  {b.selections_count > 0 ? ` · ${b.selections_count} service${b.selections_count > 1 ? "s" : ""}` : ""}
                                </p>
                              </div>
                              <span className="text-xs text-primary font-medium">View →</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Start new */}
                  <div className="pt-4 border-t border-border/30">
                    <button
                      type="button"
                      onClick={() => { setShowWelcomeBack(false); setExistingBooking(null); setSubmittedBookings([]); }}
                      data-testid="start-new-booking-btn"
                      className="w-full btn-agent-primary px-4 py-3 rounded-lg text-sm font-medium"
                    >
                      Start a New Booking
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {agent.agent_type === "yacht_charter_company" 
                    ? "Villa or Hotel Name" 
                    : (agent.villa_field_label || 'Villa Name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={villaName}
                  onChange={(e) => setVillaName(e.target.value)}
                  data-testid="villa-name-input"
                  className="form-input"
                  placeholder={agent.agent_type === "yacht_charter_company" 
                    ? "e.g., Hotel Belmond or Villa Son Vida"
                    : `e.g., ${agent.villa_field_label ? 'Your accommodation name' : 'Villa Son Vida'}`}
                  required
                />
              </div>

              {/* Villa Address & Link (optional) */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Villa Address (if known)
                </label>
                <input
                  type="text"
                  value={villaAddress}
                  onChange={(e) => setVillaAddress(e.target.value)}
                  data-testid="villa-address-input"
                  className="form-input"
                  placeholder="e.g., Calle Poligono 3, 521, Illes Balears, 07108"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Link to villa on agent website (if known)
                </label>
                <input
                  type="url"
                  value={villaLink}
                  onChange={(e) => setVillaLink(e.target.value)}
                  data-testid="villa-link-input"
                  className="form-input"
                  placeholder="e.g., https://www.amaselections.com/villa-name"
                />
              </div>

              {/* Only show Booking Reference for Villa Agents */}
              {agent.agent_type !== "yacht_charter_company" && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Booking Reference (optional)
                  </label>
                  <input
                    type="text"
                    value={bookingReference}
                    onChange={(e) => setBookingReference(e.target.value)}
                    data-testid="booking-reference-input"
                    className="form-input"
                    placeholder="e.g., BK12345"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DatePickerField
                  value={arrivalDate}
                  onChange={setArrivalDate}
                  label="Arrival Date"
                  testId="arrival-date-input"
                  required
                />
                <DatePickerField
                  value={departureDate}
                  onChange={setDepartureDate}
                  minDate={arrivalDate || undefined}
                  label="Departure Date"
                  testId="departure-date-input"
                  required
                />
              </div>

              {/* Group Size Section */}
              <div className="pt-2">
                <label className="block text-sm font-medium text-foreground mb-3">
                  Who is travelling? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {/* Adults */}
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                    <span className="text-sm font-medium text-foreground">Adults</span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setNumAdults(Math.max(1, numAdults - 1))}
                        data-testid="adults-decrease-btn"
                        className="w-8 h-8 rounded-full bg-background border border-border flex items-center justify-center text-foreground hover:bg-secondary transition-colors"
                        disabled={numAdults <= 1}
                      >
                        −
                      </button>
                      <span className="w-6 text-center font-medium text-foreground" data-testid="adults-count">
                        {numAdults}
                      </span>
                      <button
                        type="button"
                        onClick={() => setNumAdults(numAdults + 1)}
                        data-testid="adults-increase-btn"
                        className="w-8 h-8 rounded-full bg-background border border-border flex items-center justify-center text-foreground hover:bg-secondary transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Children */}
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                    <span className="text-sm font-medium text-foreground">Children</span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setNumChildren(Math.max(0, numChildren - 1))}
                        data-testid="children-decrease-btn"
                        className="w-8 h-8 rounded-full bg-background border border-border flex items-center justify-center text-foreground hover:bg-secondary transition-colors"
                        disabled={numChildren <= 0}
                      >
                        −
                      </button>
                      <span className="w-6 text-center font-medium text-foreground" data-testid="children-count">
                        {numChildren}
                      </span>
                      <button
                        type="button"
                        onClick={() => setNumChildren(numChildren + 1)}
                        data-testid="children-increase-btn"
                        className="w-8 h-8 rounded-full bg-background border border-border flex items-center justify-center text-foreground hover:bg-secondary transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Last-minute fee notice */}
              {lastMinuteBlocked && (
                <div 
                  className="mt-4 rounded-xl border border-red-300/50 bg-red-50/80 dark:bg-red-950/30 dark:border-red-800/50 p-5"
                  data-testid="last-minute-blocked-notice"
                >
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800 dark:text-red-200 leading-relaxed">
                      {lastMinuteConfig?.block_message || "Thank you for getting in touch. For arrangements with less than 24 hours notice please contact us directly at hello@myconciergeportal.com and we will do everything we can to help."}
                    </p>
                  </div>
                </div>
              )}

              {lastMinuteTier && !lastMinuteBlocked && (
                <div 
                  className="mt-4 rounded-xl border border-amber-300/50 bg-amber-50/80 dark:bg-amber-950/30 dark:border-amber-800/50 p-5"
                  data-testid="last-minute-fee-notice"
                >
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="space-y-3">
                      <p className="text-sm text-amber-900 dark:text-amber-100 leading-relaxed">
                        {(lastMinuteConfig?.fee_message_template || "Please note: as your arrival is within {days} days, a last minute planning fee of €{fee} applies. This covers the additional coordination required to arrange your bookings at short notice. By clicking Continue you agree to this fee.")
                          .replace("{fee}", lastMinuteTier.fee?.toLocaleString())
                          .replace("{days}", lastMinuteTier.daysAway)}
                      </p>
                      {!lastMinuteAccepted && (
                        <button
                          type="button"
                          onClick={() => setLastMinuteAccepted(true)}
                          data-testid="accept-last-minute-fee-btn"
                          className="text-sm font-semibold px-5 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                        >
                          I understand — Continue
                        </button>
                      )}
                      {lastMinuteAccepted && (
                        <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300 font-medium">
                          <CheckCircle2 className="w-4 h-4" />
                          Fee of €{lastMinuteTier.fee?.toLocaleString()} acknowledged
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={handleAccessPortal}
                disabled={isSubmitting || lastMinuteBlocked || (lastMinuteTier && !lastMinuteAccepted)}
                data-testid="access-portal-btn"
                className="btn-agent-primary mt-6 w-full"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  "Plan my stay →"
                )}
              </button>

              {/* Save and continue later */}
              <button
                type="button"
                onClick={handleSaveAndContinueLater}
                disabled={isSavingLater}
                data-testid="save-continue-later-btn"
                className="w-full sm:w-auto text-center text-sm font-medium transition-all duration-200 py-2.5 px-6 flex items-center justify-center gap-2 rounded border-2 mt-3"
                style={{
                  borderColor: '#b8860b',
                  color: '#b8860b',
                  background: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#b8860b';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#b8860b';
                }}
              >
                {isSavingLater ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Save and continue later →
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* Confirmation Step - After initial form submission */}
        {step === "confirmation" && submittedData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center justify-center text-center py-16"
          >
            <h2 className="text-2xl md:text-3xl font-serif text-foreground mb-6">
              Thank you, {submittedData.firstName}.
            </h2>
            <p className="text-base text-muted-foreground max-w-md mb-10 leading-relaxed" style={{ textWrap: 'pretty' }}>
              {agent.custom_portal_intro_text 
                ? agent.custom_portal_intro_text
                : agent.agent_type === "yacht_charter_company"
                  ? "We're ready when you are. Select the services you'd like and we'll prepare a personalised proposal for your stay on the island."
                  : `We're ready when you are. Select the services you'd like and we'll prepare a personalised proposal for your stay at ${submittedData.villaName}.`}
            </p>
            <button
              onClick={handleProceedToServices}
              data-testid="start-planning-btn"
              className="btn-agent-primary px-8"
            >
              {agent.agent_type === "yacht_charter_company" 
                ? "Browse Concierge Services" 
                : "Start planning your stay"}
            </button>
          </motion.div>
        )}
      </main>
      
      {/* Dynamic Legal Footer */}
      <GuestPortalFooter 
        poweredByBrand={agent?.powered_by_brand || "my_concierge_portal"}
        privacyPolicyUrl={agent?.privacy_policy_url || "https://myworldvip.com/privacy-policy"}
        termsConditionsUrl={agent?.terms_conditions_url || "https://myworldvip.com/terms-and-conditions"}
      />
    </div>
  );
}
